/**
 * @file src/shell/Whiteboard.js
 * BẢNG TRẮNG — bấm nút "Bảng trắng" ở Header thì chia đôi màn hình: sân khấu 3D co
 * lại bên trái, panel vẽ hiện ra bên phải, kéo được thanh chia để đổi tỉ lệ.
 *
 * QUAN TRỌNG — panel là SIBLING FLEX THẬT của .ol-stage, KHÔNG PHẢI overlay tuyệt đối
 * đè lên trên. Lớp này bọc .ol-stage vào một wrapper hàng ngang mới (.ol-stagerow) rồi
 * chèn panel làm anh em của nó. Nếu làm overlay (position:absolute che nửa phải màn
 * hình) thì canvas WebGL vẫn render theo bề rộng ĐẦY ĐỦ ở bên dưới — lặp lại đúng lớp
 * lỗi lệch tâm đã từng gặp với bottom bar (xem lịch sử review), lần này theo chiều
 * NGANG: mô hình sẽ lệch tâm sang trái so với phần khung nhìn còn hiện ra bên trái
 * whiteboard. Dùng flex sibling thật thì Engine._resize() (ResizeObserver quan sát
 * canvas.parentElement = .ol-stage) tự phát hiện bề rộng mới và tự canh lại — không
 * cần đụng gì tới Engine.js.
 *
 * HỆ QUẢ: ToolRail/ZoomSlider/panelHost (Info·Settings) trước đây neo theo .ol-root
 * (đúng vì trước khi có whiteboard, "vùng dưới header trên thanh bước" CHÍNH LÀ toàn
 * bộ .ol-stage). Giờ chúng phải neo theo .ol-stage thay vì .ol-root — không thì khi mở
 * whiteboard, cột công cụ/chấm zoom vẫn kéo dài che lên panel mới (xem Shell.js đã đổi
 * `root` truyền vào createToolRail/createZoomSlider/panelHost thành `o.stage`, và
 * shell.css đã bỏ offset --ol-bar-h/--ol-bottom-h ở top/bottom của ba class đó vì hộp
 * chứa bây giờ ĐÃ tự trừ header/footer rồi, không cần trừ lần hai).
 *
 * Canvas vẽ dùng chung lõi DrawCanvas.js với PenLayer.js nhưng là MỘT LỚP HOÀN TOÀN
 * KHÁC — vẽ trong whiteboard không liên quan gì tới nét bút đè trên mô hình 3D. Nội
 * dung whiteboard KHÔNG bị xoá khi đóng/mở lại (khác PenLayer cố ý) — "bảng" ngầm định
 * là thứ giữ nguyên nội dung khi tạm ẩn, còn "bút" ngầm định là ghi chú dùng một lần.
 */

import { createColorSwatches, createDrawCanvas } from './DrawCanvas.js';
import { icon } from './icons.js';

const DEFAULT_WIDTH_RATIO = 0.42;
const MIN_WIDTH = 260;
const MAX_WIDTH_RATIO = 0.7;

/**
 * @param {Object} o
 * @param {HTMLElement} o.stage
 * @param {(k: string, v?: any) => string} o.t
 * @param {() => void} [o.onClose] Panel tự đóng (nút ×) — để Shell đồng bộ ngược Header.
 */
export function createWhiteboard(o) {
  const stageParent = /** @type {HTMLElement} */ (o.stage.parentElement);

  const row = document.createElement('div');
  row.className = 'ol-stagerow';
  stageParent.insertBefore(row, o.stage);
  row.appendChild(o.stage);

  const resizer = document.createElement('div');
  resizer.className = 'ol-whiteboard-resizer';
  row.appendChild(resizer);

  const panel = document.createElement('div');
  panel.className = 'ol-whiteboard';
  panel.innerHTML = `
    <div class="ol-whiteboard__head">
      <span class="ol-icon">${icon('penSquare')}</span>
      <span class="ol-whiteboard__title"></span>
      <button type="button" class="ol-whiteboard__close"><span class="ol-icon">${icon('close')}</span></button>
    </div>
    <div class="ol-whiteboard__toolbar"></div>
    <div class="ol-whiteboard__canvas"></div>`;
  row.appendChild(panel);

  const titleEl = /** @type {HTMLElement} */ (panel.querySelector('.ol-whiteboard__title'));
  const closeBtn = /** @type {HTMLButtonElement} */ (panel.querySelector('.ol-whiteboard__close'));
  const toolbar = /** @type {HTMLElement} */ (panel.querySelector('.ol-whiteboard__toolbar'));
  const canvasWrap = /** @type {HTMLElement} */ (panel.querySelector('.ol-whiteboard__canvas'));

  const draw = createDrawCanvas({
    mount: canvasWrap,
    className: 'ol-whiteboard__ink',
    onHistoryChange: () => syncHistoryButtons(),
  });
  const { swatchesEl, customEl } = createColorSwatches({ onChange: (c) => draw.setColor(c) });

  /** @param {string} name @returns {HTMLButtonElement} Nút icon-only cho toolbar (Lùi/Làm lại/Lưu). */
  function iconButton(name) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'ol-whiteboard__iconbtn';
    b.innerHTML = `<span class="ol-icon">${icon(name)}</span>`;
    return b;
  }
  const undoBtn = iconButton('undo');
  const redoBtn = iconButton('redo');
  const sep1 = document.createElement('span');
  sep1.className = 'ol-pen-palette__sep';
  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'ol-pen-palette__clear';
  const sep2 = document.createElement('span');
  sep2.className = 'ol-pen-palette__sep';
  const saveBtn = iconButton('download');
  toolbar.append(swatchesEl, customEl, sep1, undoBtn, redoBtn, sep2, clearBtn, saveBtn);

  function syncHistoryButtons() {
    undoBtn.disabled = !draw.canUndo();
    redoBtn.disabled = !draw.canRedo();
  }
  syncHistoryButtons();

  function retranslate() {
    titleEl.textContent = o.t('header.whiteboard');
    const closeText = o.t('wb.close');
    closeBtn.title = closeText;
    closeBtn.setAttribute('aria-label', closeText);
    const clearText = o.t('pen.clear');
    clearBtn.textContent = clearText;
    clearBtn.title = clearText;
    const undoText = o.t('wb.undo');
    undoBtn.title = undoText;
    undoBtn.setAttribute('aria-label', undoText);
    const redoText = o.t('wb.redo');
    redoBtn.title = redoText;
    redoBtn.setAttribute('aria-label', redoText);
    const saveText = o.t('wb.save');
    saveBtn.title = saveText;
    saveBtn.setAttribute('aria-label', saveText);
    toolbar.setAttribute('aria-label', o.t('pen.palette'));
  }
  retranslate();

  const onClearClick = () => draw.clear();
  clearBtn.addEventListener('click', onClearClick);
  const onUndoClick = () => draw.undo();
  undoBtn.addEventListener('click', onUndoClick);
  const onRedoClick = () => draw.redo();
  redoBtn.addEventListener('click', onRedoClick);
  const onSaveClick = () => {
    // Tải file trực tiếp qua <a download> — hành động client-side do HỌC SINH tự bấm
    // trong trang, không phải Claude tải file, nên không cần hỏi xin phép gì cả.
    const a = document.createElement('a');
    a.href = draw.toDataURL();
    a.download = `bang-trang-${Date.now()}.png`;
    a.click();
  };
  saveBtn.addEventListener('click', onSaveClick);

  // Bề rộng panel khi MỞ — nhớ theo lần kéo giãn gần nhất (0 = chưa từng mở/kéo,
  // dùng mặc định DEFAULT_WIDTH_RATIO). Điều khiển bằng `width` (không phải flex-basis
  // như bản trước) để 0 → giá trị này transition được mượt — flex-basis cũng transition
  // được nhưng cả hai không nên trộn, dễ đá nhau.
  let openWidthPx = 0;

  /** @param {boolean} on */
  function setOpen(on) {
    row.classList.toggle('is-wb-open', on);
    if (on) {
      if (openWidthPx <= 0) {
        const rowW = row.clientWidth;
        const max = Math.max(MIN_WIDTH, rowW * MAX_WIDTH_RATIO);
        openWidthPx = Math.max(MIN_WIDTH, Math.min(max, rowW * DEFAULT_WIDTH_RATIO));
      }
      panel.style.width = `${openWidthPx}px`;
    } else {
      panel.style.width = '0px';
    }
  }

  const onCloseClick = () => {
    setOpen(false);
    o.onClose?.();
  };
  closeBtn.addEventListener('click', onCloseClick);

  // ---------- Kéo giãn ----------
  // setPointerCapture trên resizer (giống cách DrawCanvas bắt pointer khi vẽ) — kéo
  // vẫn nhận được dù chuột lướt ra ngoài dải mảnh 6px của resizer giữa hai lần frame.
  let dragging = false;
  let dragStartX = 0;
  let dragStartWidth = 0;
  /** @param {PointerEvent} e */
  const onResizeDown = (e) => {
    dragging = true;
    dragStartX = e.clientX;
    dragStartWidth = panel.getBoundingClientRect().width;
    resizer.setPointerCapture(e.pointerId);
    resizer.classList.add('is-dragging');
    // Tắt transition trong lúc kéo — không thì panel "đuổi theo" chuột có độ trễ
    // (mỗi lần đổi width lại chạy hết 0.32s animation), kéo thấy nặng tay, ì.
    panel.classList.add('is-resizing');
  };
  /** @param {PointerEvent} e */
  const onResizeMove = (e) => {
    // BẮT BUỘC canh dragging: pointermove nổ ra cho MỌI lần chuột lướt qua resizer,
    // KỂ CẢ khi không bấm giữ gì (hover thường). Thiếu dòng này thì dragStartX/Width
    // vẫn là 0 (giá trị khởi tạo, chưa từng kéo), dx tính ra rất lớn, panel bị co
    // thẳng về MIN_WIDTH ngay khi chuột chỉ lướt qua — đúng bug "hover là tự co lại".
    if (!dragging) return;
    // Resizer nằm ở mép TRÁI panel: kéo sang trái (dx âm) phải LÀM RỘNG panel ra, nên
    // trừ dx chứ không cộng.
    const dx = e.clientX - dragStartX;
    const max = Math.max(MIN_WIDTH, row.clientWidth * MAX_WIDTH_RATIO);
    const w = Math.max(MIN_WIDTH, Math.min(max, dragStartWidth - dx));
    openWidthPx = w;
    panel.style.width = `${w}px`;
  };
  const onResizeUp = () => {
    dragging = false;
    resizer.classList.remove('is-dragging');
    panel.classList.remove('is-resizing');
  };
  resizer.addEventListener('pointerdown', onResizeDown);
  resizer.addEventListener('pointermove', onResizeMove);
  resizer.addEventListener('pointerup', onResizeUp);
  resizer.addEventListener('pointercancel', onResizeUp);

  return {
    retranslate,
    setOpen,
    isOpen: () => row.classList.contains('is-wb-open'),
    dispose() {
      draw.dispose();
      clearBtn.removeEventListener('click', onClearClick);
      undoBtn.removeEventListener('click', onUndoClick);
      redoBtn.removeEventListener('click', onRedoClick);
      saveBtn.removeEventListener('click', onSaveClick);
      closeBtn.removeEventListener('click', onCloseClick);
      resizer.removeEventListener('pointerdown', onResizeDown);
      resizer.removeEventListener('pointermove', onResizeMove);
      resizer.removeEventListener('pointerup', onResizeUp);
      resizer.removeEventListener('pointercancel', onResizeUp);
      panel.remove();
      resizer.remove();
      // Dỡ wrapper: trả .ol-stage về đúng vị trí cũ (con trực tiếp của root) trước khi
      // gỡ .ol-stagerow — không quan trọng trong vòng đời hiện tại (root/stage không
      // tái sử dụng qua nhiều lần mount) nhưng dọn cho đối xứng với lúc dựng.
      stageParent.insertBefore(o.stage, row);
      row.remove();
    },
  };
}
